import * as Yup from "yup";

const toTime = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const time = new Date(value as string | number | Date).valueOf();
  return Number.isNaN(time) ? null : time;
};

/**
 * Mirrors the backend package validation so the form refuses what the API would refuse.
 *
 * The second round is a unit: its two dates and the first-round award cap are all set or all
 * empty. Bidding always closes before the block starts, and the block is not required to align
 * to a calendar year.
 */
export const VacationBidPackageSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .min(1, "Title is required")
    .max(60, "Title cannot exceed 60 characters"),

  bidWindowOpen: Yup.date().nullable().required("Bid Window Open is required"),

  bidWindowClose: Yup.date()
    .nullable()
    .required("Bid Window Close is required")
    .test("afterOpen", "Must be after bid window open", (date, y) => {
      const open = toTime(y.parent?.bidWindowOpen);
      const close = toTime(date);
      return open === null || close === null || close > open;
    }),

  secondBidWindowOpen: Yup.date()
    .nullable()
    .test("afterFirstClose", "Cannot open before the first bid window closes", (date, y) => {
      const open = toTime(date);
      const firstClose = toTime(y.parent?.bidWindowClose);
      return open === null || firstClose === null || open >= firstClose;
    })
    .test("pairedWithClose", "Set both second bid window dates, or neither", (date, y) => {
      const open = toTime(date);
      const close = toTime(y.parent?.secondBidWindowClose);
      return (open === null) === (close === null);
    }),

  secondBidWindowClose: Yup.date()
    .nullable()
    .test("afterSecondOpen", "Must be after second bid window open", (date, y) => {
      const open = toTime(y.parent?.secondBidWindowOpen);
      const close = toTime(date);
      return open === null || close === null || close > open;
    }),

  maxAwardsFirstRound: Yup.number()
    .nullable()
    .integer("Must be a whole number")
    .min(1, "Must be at least 1")
    .test("requiredWithSecondRound", "Required when a second bid window is set", (value, y) => {
      const hasSecondRound = toTime(y.parent?.secondBidWindowOpen) !== null;
      return !hasSecondRound || (value !== null && value !== undefined);
    })
    .test("emptyWithoutSecondRound", "Only applies when a second bid window is set", (value, y) => {
      const hasSecondRound = toTime(y.parent?.secondBidWindowOpen) !== null;
      return hasSecondRound || value === null || value === undefined;
    }),

  bidBlockStart: Yup.date()
    .nullable()
    .required("Bid Block Start is required")
    .test("afterBidding", "Bidding must close before the bid block starts", (date, y) => {
      const start = toTime(date);
      const lastClose = toTime(y.parent?.secondBidWindowClose) ?? toTime(y.parent?.bidWindowClose);
      return start === null || lastClose === null || start > lastClose;
    }),

  bidBlockEnd: Yup.date()
    .nullable()
    .required("Bid Block End is required")
    .test("afterStart", "Must be after bid block start", (date, y) => {
      const start = toTime(y.parent?.bidBlockStart);
      const end = toTime(date);
      return start === null || end === null || end > start;
    }),

  crewGroups: Yup.array().of(Yup.string()),
});

/**
 * Validation for the generate-slots dialog. Slots must not overlap, so the repeat interval can
 * never be shorter than the slot length.
 */
export const SlotGenerationSchema = Yup.object().shape({
  firstSlotStart: Yup.string().required("First slot start is required"),
  slotLengthDays: Yup.number()
    .required("Slot length is required")
    .integer("Must be a whole number")
    .min(1, "Must be at least 1 day"),
  repeatEveryDays: Yup.number()
    .required("Repeat interval is required")
    .integer("Must be a whole number")
    .min(1, "Must be at least 1 day")
    .test("noOverlap", "Slots would overlap - repeat must be at least the slot length", (value, y) => {
      const length = Number(y.parent?.slotLengthDays);
      return value === undefined || value === null || Number.isNaN(length) || value >= length;
    }),
  defaultCapacity: Yup.number()
    .required("Default capacity is required")
    .integer("Must be a whole number")
    .min(0, "Cannot be negative"),
});
